// frontend/src/components/CertificatesTab.js

import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { PlusIcon, XMarkIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

const CertificatesTab = forwardRef(({
  onDirtyChange,
  onSummaryChange,
  initialData,
}, ref) => {
  const [certificates, setCertificates] = useState([]);

  // Load initial data
  useEffect(() => {
    if (initialData?.certificatesList) {
      setCertificates(initialData.certificatesList);
    }
  }, [initialData]);

  useEffect(() => {
    const itemCount = certificates.filter(c => c && c.name && c.name.trim() !== '').length;
    onSummaryChange?.({ itemCount });
  }, [certificates, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      certificatesList: certificates.filter(c => c && c.name && c.name.trim() !== ''),
    }),
    resetDirty: () => {},
  }));

  const addCertificate = () => {
    setCertificates([...certificates, { name: "", type: "health", issuedDate: "", fileUrl: "" }]);
    onDirtyChange?.(true);
  };

  const updateCertificate = (index, field, value) => {
    const updated = [...certificates];
    updated[index] = { ...updated[index], [field]: value };
    setCertificates(updated);
    onDirtyChange?.(true);
  };

  const removeCertificate = (index) => {
    setCertificates(certificates.filter((_, i) => i !== index));
    onDirtyChange?.(true);
  };

  const certificateTypes = ["Health", "Vaccination", "Rabies", "Travel", "Other"];

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <FieldLabel>Certificates</FieldLabel>
        <button
          type="button"
          onClick={addCertificate}
          className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 hover:text-emerald-700"
        >
          <PlusIcon className="w-4 h-4" /> Add Certificate
        </button>
      </div>

      {certificates.length === 0 ? (
        <button
          type="button"
          onClick={addCertificate}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-6 text-center hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
        >
          <div className="flex flex-col items-center gap-1">
            <DocumentTextIcon className="w-8 h-8 text-slate-300" />
            <p className="text-[12px] text-slate-400">No certificates added yet — click to add</p>
          </div>
        </button>
      ) : (
        <div className="space-y-3">
          {certificates.map((cert, idx) => (
            <div key={idx} className="flex flex-col gap-2 p-4 bg-white border border-slate-100 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-400 min-w-[24px]">
                  #{idx + 1}
                </span>
                <input
                  type="text"
                  value={cert.name || ""}
                  onChange={(e) => updateCertificate(idx, "name", e.target.value)}
                  placeholder="Enter certificate name..."
                  className="flex-1 text-[13px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => removeCertificate(idx)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-3">
                <select
                  value={cert.type || "health"}
                  onChange={(e) => updateCertificate(idx, "type", e.target.value)}
                  className="text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent bg-white"
                >
                  {certificateTypes.map((type) => (
                    <option key={type} value={type.toLowerCase()}>{type}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={cert.issuedDate || ""}
                  onChange={(e) => updateCertificate(idx, "issuedDate", e.target.value)}
                  className="text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                />
                <input
                  type="text"
                  value={cert.fileUrl || ""}
                  onChange={(e) => updateCertificate(idx, "fileUrl", e.target.value)}
                  placeholder="File URL or reference..."
                  className="flex-1 text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

CertificatesTab.displayName = "CertificatesTab";

export default CertificatesTab;